/**
 * A cooldown after a burst of the same action.
 *
 * Sending or deleting a few messages quickly is normal. Doing it twelve times
 * in a row is either a mistake or a flood, and the server's own limiter
 * answers that with an error after the fact - by which point the damage to
 * the conversation is done.
 *
 * This holds the tenth one back for a few seconds instead, which is enough to
 * break the rhythm without ever getting in the way of ordinary use.
 */

const WINDOW_MS = 10_000;
const ALLOWED = 10;
const COOLDOWN_MS = 3_000;

type Track = { times: number[]; until: number };
const tracks = new Map<string, Track>();

/**
 * Records one action and says how long to wait before it may happen.
 *
 * Returns 0 when it can go straight through, or the milliseconds remaining on
 * a cooldown. The caller decides whether to wait or refuse.
 */
export function burstDelay(key: string): number {
  const now = Date.now();
  const track = tracks.get(key) ?? { times: [], until: 0 };

  if (track.until > now) {
    tracks.set(key, track);
    return track.until - now;
  }

  track.times = track.times.filter((t) => now - t < WINDOW_MS);
  track.times.push(now);

  if (track.times.length > ALLOWED) {
    track.until = now + COOLDOWN_MS;
    track.times = [];
    tracks.set(key, track);
    return COOLDOWN_MS;
  }

  tracks.set(key, track);
  return 0;
}

/** How long is left on a cooldown, without recording anything. */
export function burstRemaining(key: string): number {
  const track = tracks.get(key);
  if (!track) return 0;
  return Math.max(0, track.until - Date.now());
}
